import type { GetCurrentSubscriptionResponse } from "@cabal/shared/domain/billing/subscription/types.js";
import { SubscriptionContractStatus } from "@cabal/shared/domain/billing/subscription/enums.js";
import type { Collab } from "@cabal/shared/domain/session/collab/types.js";
import { HaltReason, SessionStatus } from "@cabal/shared/domain/session/enums.js";
import type { ProductSummary } from "@cabal/shared/domain/session/product/types.js";
import type { SessionOverviewResponse } from "@cabal/shared/domain/session/types.js";
import type { VersionSummary } from "@cabal/shared/domain/session/version/types.js";
import type React from "react";
import type { SessionCollab } from "./context";

export const OUT_OF_CREDITS_TITLE = "Out of credits";
export const OUT_OF_CREDITS_ACKNOWLEDGE = "Acknowledge";
export const OUT_OF_CREDITS_PREFIX = "You are out of credits for your plan.";
export const OUT_OF_CREDITS_WITHOUT_ACTIVE_PLAN =
  "You should acquire a plan access to resume your session";
export const ACCOUNT_SUSPENDED_TITLE = "Account suspended";
export const ACCOUNT_SUSPENDED_BODY =
  "This account has been suspended. Contact support to restore access.";
export const ACCOUNT_SUSPENDED_ACCESS = {
  disableDismiss: true,
  redirectTo: "/",
} as const;
export const TRASPASSING_TITLE = "TRESPASSING";
export const TRASPASSING_BODY = "You do not have the clearance for this session.";
export const TRASPASSING_ACKNOWLEDGE = "Acknowledge";
export const CHAT_PAGE_LIMIT = 50;

export enum SessionUpdateSource {
  InitialLoad = "initialLoad",
  Overview = "overview",
  LocalPatch = "localPatch",
}

export enum PollingSetupSource {
  InitialEffect = "initial effect",
  RestartEffect = "restart effect",
}

export interface PollingIntervals {
  session: number;
  members: number;
  products: number;
  chat: number;
  collabs: number;
}

const LIVE_POLLING_INTERVALS: PollingIntervals = {
  session: 5000,
  members: 3000,
  products: 10000,
  chat: 5000,
  collabs: 5000,
};

const RESTING_POLLING_INTERVALS: PollingIntervals = {
  session: 15000,
  members: 60000,
  products: 60000,
  chat: 60000,
  collabs: 60000,
};

export function getHaltReason(input: {
  session: SessionOverviewResponse | null;
}): HaltReason | null {
  return input.session?.haltInfo?.reason ?? null;
}

export function isInsufficientTokensReason(input: {
  haltReason: HaltReason | null | undefined;
}): boolean {
  return input.haltReason === HaltReason.InsufficientTokens;
}

export function hasActiveSubscriptionContract(input: {
  subscription: GetCurrentSubscriptionResponse;
}): boolean {
  return (
    input.subscription.contract.status === SubscriptionContractStatus.Active
  );
}

export function isSuspendedSubscriptionContract(input: {
  subscription: GetCurrentSubscriptionResponse;
}): boolean {
  return (
    input.subscription.contract.status === SubscriptionContractStatus.Suspended
  );
}

export function buildOutOfCreditsBody(input: {
  hasActiveContract: boolean;
  currentPeriodEnd: string | null | undefined;
}): string {
  if (!input.hasActiveContract) {
    return `${OUT_OF_CREDITS_PREFIX} ${OUT_OF_CREDITS_WITHOUT_ACTIVE_PLAN}`;
  }

  if (!input.currentPeriodEnd) {
    return `${OUT_OF_CREDITS_PREFIX} Upgrade your plan, or revisit the CABAL at your renewal.`;
  }

  return `${OUT_OF_CREDITS_PREFIX} Upgrade your plan, or revisit the CABAL at your renewal at ${input.currentPeriodEnd}`;
}

export function getPollingIntervals(
  sessionStatus: SessionStatus | null,
): PollingIntervals {
  return sessionStatus === SessionStatus.Live
    ? LIVE_POLLING_INTERVALS
    : RESTING_POLLING_INTERVALS;
}

export function toSessionCollab(input: { collab: Collab }): SessionCollab {
  return input.collab;
}

export function mergeVersionMap(input: {
  previous: Record<string, VersionSummary>;
  incoming: VersionSummary[];
}): Record<string, VersionSummary> {
  const merged = { ...input.previous };
  input.incoming.forEach((version) => {
    merged[version.id] = version;
  });
  return merged;
}

export function mergeCollabMap(input: {
  previous: Record<string, SessionCollab>;
  items: readonly SessionCollab[];
}): Record<string, SessionCollab> {
  const updated = { ...input.previous };
  input.items.forEach((collab) => {
    updated[collab.id] = collab;
  });
  return updated;
}

export function selectCollabIds(items: readonly SessionCollab[]): string[] {
  return items.map((collab) => collab.id);
}

export function selectNewIds(input: {
  incomingIds: readonly string[];
  existingIds: readonly string[];
}): string[] {
  const existingIds = new Set(input.existingIds);
  return input.incomingIds.filter((id) => !existingIds.has(id));
}

export function selectAnimatedCollabArrivalIds(input: {
  newIds: readonly string[];
  ownMessageIds: ReadonlySet<string>;
  canAnimate: boolean;
}): string[] {
  if (!input.canAnimate) {
    return [];
  }

  return input.newIds.filter((id) => !input.ownMessageIds.has(id));
}

export function selectInitialChatState(collabs: readonly Collab[]): {
  chatMap: Record<string, SessionCollab>;
  chatIds: string[];
} {
  const chatMap: Record<string, SessionCollab> = {};
  const chatIds: string[] = [];
  collabs.forEach((collab) => {
    const message = toSessionCollab({ collab });
    chatMap[message.id] = message;
    chatIds.push(message.id);
  });
  return { chatMap, chatIds };
}

export function toProductMap(
  products: readonly ProductSummary[],
): Record<string, ProductSummary> {
  const productsMap: Record<string, ProductSummary> = {};
  products.forEach((product) => {
    productsMap[product.id] = product;
  });
  return productsMap;
}

export function clearPollingIntervalRefs(
  refs: readonly React.MutableRefObject<NodeJS.Timeout | null>[],
): void {
  refs.forEach((ref) => {
    if (ref.current) {
      clearInterval(ref.current);
    }
    ref.current = null;
  });
}

export function arePollingIntervalRefsRunning(
  refs: readonly React.MutableRefObject<NodeJS.Timeout | null>[],
): boolean {
  return refs.every((ref) => Boolean(ref.current));
}

export function isNotFoundError(error: unknown): boolean {
  return hasStatusCode({ error, statusCode: 404 });
}

export function isUnauthorizedError(error: unknown): boolean {
  return hasStatusCode({ error, statusCode: 401 });
}

function hasStatusCode(input: { error: unknown; statusCode: number }): boolean {
  const error = input.error;
  if (!error || typeof error !== "object") {
    return false;
  }

  return (
    "statusCode" in error &&
    typeof error.statusCode === "number" &&
    error.statusCode === input.statusCode
  );
}
